import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Input,
  ViewChild,
} from "@angular/core";
import { NG_VALUE_ACCESSOR } from "@angular/forms";
import { NgbCalendar, NgbInputDatepicker } from "@ng-bootstrap/ng-bootstrap";
import { NgbDate } from "@ng-bootstrap/ng-bootstrap/datepicker/ngb-date";
import * as moment from "moment";
import { BaseControlValueAccessor } from "../date-picker/date-picker";

@Component({
  selector: "cur-date-range-picker",
  templateUrl: "date-range-picker.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => DateRangePickerComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateRangePickerComponent
  extends BaseControlValueAccessor<Object>
  implements AfterViewInit
{
  @Input() popupPlacement: string[] = null;
  @Input() label: string = null;
  @Input() useTodayAsDefault: boolean = false;
  @Input() placeholderText: string = "MM/DD/YYYY";

  @ViewChild("dp")
  private datePicker: NgbInputDatepicker;

  hoveredDate: NgbDate;
  fromDate: NgbDate;
  toDate: NgbDate;

  constructor(private calendar: NgbCalendar) {
    super();
    this.fromDate = calendar.getToday();
    this.toDate = calendar.getToday();
  }

  ngAfterViewInit(): void {
    this.datePicker.navigateTo(this.fromDate);
  }

  onDateSelection(date: NgbDate) {
    const momentDate = [
      date.year.toString(),
      date.month.toString(),
      date.day.toString(),
    ].join("-");
    const momentValue = moment(momentDate, "YYYY-MM-DD");
    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
    } else if (this.fromDate && momentValue.isAfter(this.fromDateMomentValue)) {
      this.toDate = date;
      this.datePicker.toggle();
    } else {
      this.toDate = null;
      this.fromDate = date;
    }
    this.emitChangesToFormControl();
  }

  private emitChangesToFormControl() {
    const result: any = { toDate: null, fromDate: null };
    if (this.fromDate) {
      result.fromDate = moment({
        year: this.fromDate.year,
        month: this.fromDate.month - 1,
        day: this.fromDate.day,
      }).toString();
    }
    if (this.toDate) {
      result.toDate = moment({
        year: this.toDate.year,
        month: this.toDate.month - 1,
        day: this.toDate.day,
      }).toString();
    }
    this.onChange(result);
  }

  public writeValue(obj: any): void {
    super.writeValue(obj);
    if (obj && obj.hasOwnProperty("toDate") && obj.hasOwnProperty("fromDate")) {
      try {
        this.fromDate = new NgbDate(
          moment(obj.fromDate).year(),
          moment(obj.fromDate).month(),
          moment(obj.fromDate).date()
        );
      } catch (ex) {
        if (this.useTodayAsDefault) {
          this.fromDate = this.calendar.getToday();
        } else {
          this.fromDate = null;
        }
      }
      try {
        this.toDate = new NgbDate(
          moment(obj.toDate).year(),
          moment(obj.toDate).month(),
          moment(obj.toDate).date()
        );
      } catch (ex) {
        if (this.useTodayAsDefault) {
          this.toDate = this.calendar.getToday();
        } else {
          this.toDate = null;
        }
      }
    } else {
      this.fromDate = null;
      this.toDate = null;
    }
  }

  isHovered(date: NgbDate) {
    if (date) {
      const momentDate = [
        date.year.toString(),
        date.month.toString(),
        date.day.toString(),
      ].join("-");
      const momentValue = moment(momentDate, "YYYY-MM-DD");
      return (
        this.fromDate &&
        this.hoveredDate &&
        momentValue.isAfter(this.fromDateMomentValue) &&
        momentValue.isBefore(this.hoveredDateMomentValue)
      );
    }
    return false;
  }

  isInside(date: NgbDate) {
    if (date) {
      const momentDate = [
        date.year.toString(),
        date.month.toString(),
        date.day.toString(),
      ].join("-");
      const momentValue = moment(momentDate, "YYYY-MM-DD");
      return (
        momentValue.isAfter(this.fromDateMomentValue) &&
        momentValue.isBefore(this.toDateMomentValue)
      );
    }
    return false;
  }

  isRange(date: NgbDate) {
    if (date) {
      const momentDate = [
        date.year.toString(),
        date.month.toString(),
        date.day.toString(),
      ].join("-");
      const momentValue = moment(momentDate, "YYYY-MM-DD");
      return (
        momentValue.isSame(this.fromDateMomentValue) ||
        momentValue.isSame(this.toDateMomentValue) ||
        this.isInside(date) ||
        this.isHovered(date)
      );
    }
    return false;
  }

  get fromDateMomentValue() {
    if (this.fromDate) {
      const momentDate = [
        this.fromDate.year.toString(),
        this.fromDate.month.toString(),
        this.fromDate.day.toString(),
      ].join("-");
      return moment(momentDate, "YYYY-MM-DD");
    }
    return null;
  }

  get toDateMomentValue() {
    if (this.toDate) {
      const momentDate = [
        this.toDate.year.toString(),
        this.toDate.month.toString(),
        this.toDate.day.toString(),
      ].join("-");
      return moment(momentDate, "YYYY-MM-DD").format("MM/DD/YYYY");
    }
    return null;
  }

  get fromDateValue() {
    try {
      const momentDate = [
        this.fromDate.year.toString(),
        this.fromDate.month.toString(),
        this.fromDate.day.toString(),
      ].join("-");
      return moment(momentDate, "YYYY-MM-DD").format("MM/DD/YYYY");
    } catch (excepion) {
      return null;
    }
  }

  get hoveredDateMomentValue() {
    if (this.hoveredDate) {
      const momentDate = [
        this.hoveredDate.year.toString(),
        this.hoveredDate.month.toString(),
        this.hoveredDate.day.toString(),
      ].join("-");
      return moment(momentDate, "YYYY-MM-DD");
    }
    return null;
  }
}



// WEBPACK FOOTER //
// ./src/components/common/date-range-picker/date-range-picker.ts